import { IContextMenuState, WhatsNewTag } from 'types'
import { config } from 'config'
import { DEFAULT_HEADER_BACKGROUND_COLOUR, DEFAULT_HEADER_TEXT_COLOUR } from 'helpers/headerColours'

export const MAINTENANCE_MODE = config.MAINTENANCE_MODE

export enum ProjectRoles {
  MEMBER = 'member',
  OWNER = 'owner',
  ADMIN = 'admin'
}

// User-facing label for a hub role. The DB enum value `admin` is shown as 'Builder' in the product.
export const projectRoleLabel = (role: ProjectRoles | string): string => {
  if (role === ProjectRoles.ADMIN) return 'Builder'
  return role.charAt(0).toUpperCase() + role.slice(1)
}

export enum HubRoleAssignmentMethod {
  DIRECT = 'direct',
  ORGANISATION = 'organisation',
  SITE = 'site'
}

export enum OrganisationRoles {
  ADMIN = 'admin',
  MEMBER = 'member'
}

export enum IntegrationProviders {
  VIEWPOINT = 'viewpoint',
  AUTODESK_BIM360 = 'autodesk',
  PROCORE = 'procore',
  ACONEX = 'aconex',
  MORTA = 'morta',
  REVIZTO = 'revizto',
  ASITE = 'asite',
  SHAREPOINT = 'sharepoint',
  ASTA = 'asta',
  DALUX = 'dalux'
}

export const REVIZTO_REGIONS: Record<string, string> = {
  canada: 'Canada',
  frankfurt: 'UAE (hosted in Germany)',
  ireland: 'Europe (Ireland)',
  ksa: 'Kingdom of Saudi Arabia',
  london: 'Europe (London)',
  saopaulo: 'South America (Brazil)',
  shanghai: 'China',
  singapore: 'Southeast Asia (Singapore)',
  sydney: 'Australia',
  tokyo: 'Japan',
  virginia: 'North America (USA)',
  zurich: 'Switzerland'
}

export enum IntegrationSyncResourceTypes {
  PROJECTS = 'hubs',
  RESOURCES = 'resources',
  USERS = 'users',
  DOCUMENTS = 'documents',
  WORKFLOWS = 'workflows',
  COMMENTS = 'comments',
  VIEWS = 'table data',
  RFIS = 'rfis',
  CHECKLISTS = 'checklists',
  COLUMNS = 'columns',
  ISSUES = 'issues',
  FORMS = 'forms',
  FORMS_HEADERS = 'forms_headers',
  FORMS_FIELDS = 'forms_fields',
  FORMS_TEMPLATES = 'forms_templates',
  REVIEWS = 'reviews',
  SHEETS = 'sheets',
  MEMBERS = 'members',
  MODELS = 'models',
  TASKS = 'tasks',
  TASKS_HEADERS = 'tasks_headers',
  TASKS_FIELDS = 'tasks_fields',
  WORKPACKAGES = 'workpackages',
  INSPECTION_PLANS = 'inspection_plans',
  INSPECTION_PLAN_ITEMS = 'inspection_plan_items',
  LOCATIONS = 'locations'
}

export enum UserRoles {
  NO_ACCEESS = -1,
  VIEWER = 0,
  NOTIFY = 1,
  CONTRIBUTOR = 2,
  REVIEWER = 3,
  OWNER = 4
}

export enum AccessResourceKinds {
  TABLE = 'table',
  PROCESS = 'process',
  VIEW = 'table_view'
}

export enum AccessAttributeKinds {
  USER = 'user',
  TAG = 'tag',
  PROJECT = 'project',
  ALL_TABLE_TAGS = 'all_table_tags'
}

export const INITIAL_PROJECT_INFORMATION = {
  createdAt: '2020-06-25T15:04:17.642721',
  deletedAt: null,
  isDeleted: false,
  name: '',
  primaryColour: '#2B3389',
  fontColour: '#ffffff',
  projectList: [],
  publicId: '',
  updatedAt: '2021-06-07T14:16:00.517533',
  loading: true,
  role: ProjectRoles.MEMBER,
  defaultProcessId: '',
  allowTableExport: true,
  allowDocumentExport: true,
  hubFolders: [],
  sidebarResources: [],
  logo: null,
  defaultBanner: null,
  defaultHeaderBackgroundColor: DEFAULT_HEADER_BACKGROUND_COLOUR,
  defaultHeaderTextColor: DEFAULT_HEADER_TEXT_COLOUR,
  wordTemplate: null,
  wordTableStyle: null,
  wordTableStyles: null,
  processTitleFontSize: 1.4,
  processTitleBold: true,
  processTitleItalic: false,
  processTitleUnderline: false,
  processTitleColour: '#000000',
  defaultDateFormat: 'YYYY-MM-DD',
  defaultDatetimeFormat: 'YYYY-MM-DD HH:mm:ss',
  processTitleAlignment: 'left' as 'left' | 'center' | 'right',
  duplicatedChildren: [],
  headingStyles: [],
  public: false,
  domainsAccess: [],
  mfaRequired: false,
  hideProcessCreated: false,
  disablePersonalViewSaving: false,
  site: null
}

export const INITIAL_CONTEXT_MENU_STATE: IContextMenuState = {
  open: false,
  top: '0px',
  right: '0px',
  left: '0px',
  bottom: '0px',
  columnNumber: -1,
  columnId: '',
  rowNumber: -1,
  rowId: ''
}

export const WHATS_NEW_STORAGE_KEY = 'whatsNewLastSeen'
export const WHATS_NEW_VISIBLE_RELEASES = 3
export const WHATS_NEW_TAG_LABELS: Record<WhatsNewTag, string> = {
  new_feature: 'New Feature',
  improvement: 'Improvement',
  announcement: 'Announcement'
}

export const DEFAULT_SIDEBAR_WIDTH = 240
// The narrowest the sidebar goes while you drag it: below this, hub and resource names are cut
// down to nothing. Drag on past SIDEBAR_COLLAPSE_WIDTH and it closes instead, rather than
// leaving a slim strip you can't read.
export const SIDEBAR_MIN_WIDTH = 200
export const SIDEBAR_COLLAPSE_WIDTH = 140
// The width a hidden sidebar reopens at, kept across reloads.
export const SIDEBAR_EXPANDED_WIDTH_KEY = 'mortaSidebarExpandedWidth'
// The site sections folded away in the hub list, kept across reloads.
export const SIDEBAR_COLLAPSED_SITES_KEY = 'mortaSidebarCollapsedSites'

// Hub folder limits the server enforces.
// How deep folders may nest; a top-level folder is depth 1.
// Keep in step with `MAX_HUB_FOLDER_DEPTH` in api/app/constants.py.
export const MAX_HUB_FOLDER_DEPTH = 5
// The most containers (folders, or the hub's top level) one resources_updated event names, and one sidebar
// container read asks for. Keep in step with `MAX_SIDEBAR_CONTAINERS` in api/app/constants.py.
export const MAX_SIDEBAR_CONTAINERS = 50
// The most folders, documents and tables one move may carry; useHubFolders sends more in batches.
// Keep in step with `MAX_HUB_FOLDER_MOVE_ITEMS` in api/app/constants.py.
export const MAX_HUB_FOLDER_MOVE_ITEMS = 500

export const USER_SUBSCRIPTION_LEVELS = ['Free', 'Paid']

export const ORG_PLAN_TIERS = {
  PROFESSIONAL: 'professional',
  TEAM: 'team',
  SCALE_UP: 'scale_up'
} as const

export const AUDIT_RED_KEYWORDS = ['error', 'deleted', 'truncated']
export const AUDIT_GREEN_KEYWORDS = [
  'success',
  'created',
  'updated',
  'restored',
  'duplicated',
  'uploaded',
  'reopened',
  'completed',
  'run'
]

export const PROJECT = 'hub'

// The organisation setting that restricts who can create hubs, shown in Security Settings and the Admin panel.
export const HUB_CREATION_RESTRICTION_LABEL = `Only admins can create or duplicate ${PROJECT}s`
export const HUB_CREATION_RESTRICTION_DESCRIPTION =
  `Organisation Admins can always create ${PROJECT}s, in any Site. Site Admins can create them in the Sites they ` +
  `administer, and Team Managers in the Sites that belong to their team. Every new ${PROJECT} must belong to a Site. ` +
  `Existing ${PROJECT}s are not affected, but one that isn't in a Site can't be duplicated until it's given one.`
// The organisation setting that restricts who can set up syncs, shown beside it in the same two places.
export const SYNC_CONFIGURATION_RESTRICTION_LABEL = 'Only admins can set up, change or remove syncs'
/** Shown on a disabled sync control where the organisation only lets Site Admins configure syncs. */
export const SYNC_CONFIGURATION_RESTRICTED_MESSAGE = 'You need to be a Site Admin to set up, change or remove a sync'
export const SYNC_CONFIGURATION_RESTRICTION_DESCRIPTION =
  `Covers imports from outside systems and from other ${PROJECT}s. Organisation Admins can always configure syncs. ` +
  `Site Admins can configure them in the Sites they administer, and Team Managers in the Sites that belong to their ` +
  `team. Existing syncs keep running.`
export const TEAM_SCALE_PLAN = 'Team & Scale Up'

// Mirrors ``RESTRICTED_SOURCE_PLACEHOLDER`` in ``api/app/constants.py``: shown in place of the name of a
// source table/view/column the user can't read, so a configured source isn't mistaken for an empty one.
export const RESTRICTED_SOURCE_PLACEHOLDER = 'Restricted source'

// Mirrors ``SPECIAL_CHARS`` in ``api/app/services/password_policy.py``. Keep
// in sync: any character the backend accepts must also be accepted client-
// side, otherwise valid passwords are rejected before they reach the server.
// ``-`` is kept last so the character class ``[${...}]`` treats it as a
// literal rather than a range. See issue #9865.
export const ALLOWED_SPECIAL_CHARACTERS_PASSWORD = '#?!@$%^&*-'

export const REGEX_SPECIAL_CHARACTERS_PASSWORD = new RegExp(`^(?=.*[${ALLOWED_SPECIAL_CHARACTERS_PASSWORD}])`)

// Minimum password length, mirrored from the backend policy in
// ``api/app/services/password_policy.py`` (MIN_PASSWORD_LENGTH). Kept in
// sync so signup, reset, account-change, and admin-set forms do not
// advertise a weaker rule than the server enforces. See issue #9865.
export const MIN_PASSWORD_LENGTH = 12

export const REGEX_PASSWORD_MIN_LENGTH = new RegExp(`.{${MIN_PASSWORD_LENGTH},}`)

export const APP_URL = `${window.location.protocol}//${window.location.hostname}${
  window.location.port ? `:${window.location.port}` : ''
}`

export const DEFAULT_AVATAR_PATH = `${APP_URL}/assets/images/user.webp`
export const AUTH_KEY = 'morta_user'
// Marks the web app's section saves. The API refuses a section save without it that would remove a
// table converted from a document response: such a save comes from a tab opened before the conversion.
// A query parameter rather than a header, which every backend's CORS allowlist would have to accept.
export const WEB_CLIENT_QUERY = 'client=web'
export const SIGNIN_EMAIL_KEY = 'morta_signin_email'
export const TOUR_STORAGE_KEY = 'morta_active_tour'
export const TOUR_STEP_STORAGE_KEY = 'morta_tour_current_step'
export const FREE_TRIAL_BANNER_DISMISSED_KEY = 'freeTrialBannerDismissed'
export const AUDIT_LOGS_BLUR_DAYS: Record<string, number> = {
  [ORG_PLAN_TIERS.PROFESSIONAL]: 30,
  [ORG_PLAN_TIERS.TEAM]: 365,
  [ORG_PLAN_TIERS.SCALE_UP]: 5 * 365
}

export const BACKUP_ALLOWED_DAYS: Record<string, number> = {
  [ORG_PLAN_TIERS.PROFESSIONAL]: 30,
  [ORG_PLAN_TIERS.TEAM]: 365,
  [ORG_PLAN_TIERS.SCALE_UP]: 5 * 365
}
